











































import {mapActions, mapGetters} from 'vuex';
import {validationMixin} from 'vuelidate';
import {maxLength, required} from 'vuelidate/lib/validators';
import Vue from 'vue';
import {TranslateResult} from 'vue-i18n';
import {CategoryAPI} from '#/Category';

interface DataDialog {
  actions: string;
  body: {
    type: string;
    label: TranslateResult;
  };
  display: boolean;
  text: TranslateResult;
  title: TranslateResult;
}

interface DataCategory {
  id: number | null;
  name: string;
  parentCategory: {
    id: number | null;
  };
}

interface ComponentData {
  dialog: DataDialog;
  category: DataCategory;
}

export default Vue.extend({
  name: 'AdminCategory',

  mixins: [validationMixin],

  data: (): ComponentData => ({
    dialog: {
      actions: '',
      body: {
        type: '',
        label: '',
      },
      display: false,
      text: '',
      title: '',
    },
    category: {
      id: null,
      name: '',
      parentCategory: {
        id: null,
      },
    },
  }),
  computed: {
    ...mapGetters([
      'categories',
    ]),
    daysTypeErrors(): TranslateResult[] {
      const errors: TranslateResult[] = [];
      if (!this.$v?.category?.name?.$dirty) return errors;
      if (!this.$v.category.name.required) {
        errors.push(this.$t('admin.addCategory.form.errors.required'));
      }
      if (!this.$v.category.name.maxLength) {
        errors.push(this.$t('admin.addCategory.form.errors.maxLength'));
      }
      return errors;
    },
  },

  validations: {
    category: {
      name: {
        required,
        maxLength: maxLength(255),
      },
    },
  },

  created() {
    this.updatePageTitle(this.$t('admin.addCategory.pageTitle'));
    this.loadCategories();
  },

  methods: {
    ...mapActions([
      'createCategory',
      'deleteCategory',
      'loadCategories',
      'updateCategory',
      'updatePageTitle',
    ]),
    onAddCategory(): void {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.createCategory({
          name: this.category.name,
          parentCategory: this.category.parentCategory.id,
        })
            .then(() => {
              this.dialog.display = false;
              this.onResetForm();
            });
      }
    },
    onAddDialog({id, name}: CategoryAPI): void {
      this.category.parentCategory.id = id;
      this.dialog.title = this.$t('admin.addCategory.form.title.add');
      this.dialog.text = this.$t('admin.addCategory.form.text.add', {name});
      this.dialog.body.type = `input`;
      this.dialog.body.label = this.$t('admin.addCategory.form.label.add');
      this.dialog.actions = 'add';
      this.dialog.display = true;
    },
    onDeleteCategory(): void {
      this.deleteCategory(this.category.id)
          .then(() => {
            this.dialog.display = false;
            this.onResetForm();
          });
    },
    onDeleteDialog({id, name, children}: CategoryAPI): void {
      this.dialog.title = this.$t('admin.addCategory.form.title.delete');
      this.dialog.text = this.$t('admin.addCategory.form.text.delete', {name});
      this.dialog.body.type = `text`;

      // calcule le nombre de categories filles pour l'ajouter au message de confirmation
      const childrenCount = children
          ? children.reduce((acc, cur) => acc + (cur.children ? cur.children.length : 0) + 1, 0)
          : 0;
      this.dialog.body.label = (this.$t('admin.addCategory.form.label.delete.base') as string)
          .concat(
              childrenCount
                  ? this.$t('admin.addCategory.form.label.delete.extra', {count: childrenCount?.toString()}) as string
                  : ' ?',
          );

      this.category.id = id;
      this.dialog.actions = 'delete';
      this.dialog.display = true;
    },
    onResetForm(): void {
      this.$v.$reset();
      this.dialog = {
        actions: '',
        body: {
          type: '',
          label: '',
        },
        display: false,
        text: '',
        title: '',
      };

      this.category = {
        id: null,
        name: '',
        parentCategory: {
          id: null,
        },
      };
    },
    onUpdateCategory(): void {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        const {id, name} = this.category;

        this.updateCategory({id, name})
            .then(() => {
              this.dialog.display = false;
              this.onResetForm();
            });
      }
    },
    onUpdateDialog({id, name}: CategoryAPI): void {
      this.dialog.title = this.$t('admin.addCategory.form.title.update');
      this.dialog.text = this.$t('admin.addCategory.form.text.update', {name});
      this.dialog.body.type = `input`;
      this.dialog.body.label = this.$t('admin.addCategory.form.label.update');
      this.category.id = id;
      this.category.name = name;
      this.dialog.actions = 'update';
      this.dialog.display = true;
    },
  },
});
